/**
 * Turret laser FX — the track → charge → fire choreography for the lock-on
 * turret (family `turret`). *Core decides, Phaser renders*: the deterministic sim
 * owns the beam geometry, the lock timing (`aimLockMs`) and the lethal window; this
 * paints them so the attack READS and can be dodged.
 *
 * Phases (derived from the hazard's `bornMs` / `aimLockMs` / `telegraphMs` /
 * `activeMs`, so live play and replays look identical):
 *  - TRACK  `[bornMs, bornMs+aimLockMs)` — the beam re-aims at the player each step
 *    (core); we draw a faint targeting line, the teal turret body at the muzzle, and
 *    a GOLD lock-on reticle riding on the player.
 *  - CHARGE `[+aimLockMs, +telegraphMs)` — the aim is FROZEN (core stopped re-aiming),
 *    so we lock the reticle on the player's last-tracked spot (it reddens + tightens),
 *    brighten/pulse the beam, grow a muzzle glow and crackle charge sparks. The player
 *    can now dodge OFF the locked line.
 *  - FIRE   `[+telegraphMs, +telegraphMs+activeMs)` — the lethal red lance snaps to
 *    full width with a white muzzle flash + a forward spark burst and an impact flare
 *    on the locked point.
 *
 * Purely decorative + pooled (no per-spawn allocation): one turret body, reticle,
 * beam and muzzle-glow image per live turret id, plus one shared spark emitter.
 */
import Phaser from 'phaser';
import type { SegmentHazard, Viewport } from '../core';
import { TURRET_BODY } from '../core';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

/** Author-time pixel sizes (match textures.ts). */
const BEAM_TEX_W = 16;
const BEAM_TEX_H = 64;

/** Render depths: beam under the body so the beam reads as leaving the barrel. */
const BEAM_DEPTH = 5;
const BODY_DEPTH = 6;
const RETICLE_DEPTH = 7;
const GLOW_DEPTH = 8;

/** How many charge sparks per crackle burst, and the crackle interval (ms). */
const CHARGE_SPARKS = 4;
const CHARGE_INTERVAL_MS = 70;
/** Forward spark count on the fire flash. */
const FIRE_SPARKS = 18;

interface Vec {
  x: number;
  y: number;
}

/** How far (px) the turret barrel tip reaches from the mounted body (the beam root). */
const BARREL_LEN = 28;

/** The leased visuals + per-id phase state for one turret. */
interface TState {
  body: Phaser.GameObjects.Image;
  /** The pool the body came from (manta vs cannon) — released back to the same one. */
  bodyPool: Pool;
  reticle: Phaser.GameObjects.Image;
  beam: Phaser.GameObjects.Image;
  glow: Phaser.GameObjects.Image;
  /** The player's position snapshotted at the lock frame (the charge/fire target). */
  locked: Vec | null;
  /** Edge guard so the fire burst fires exactly once. */
  fired: boolean;
}

/** A tiny free-list of images sharing one texture + depth. */
class Pool {
  private readonly free: Phaser.GameObjects.Image[] = [];
  private readonly all: Phaser.GameObjects.Image[] = [];
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly tex: string,
    private readonly depth: number,
    private readonly blend: Phaser.BlendModes = Phaser.BlendModes.NORMAL,
  ) {}
  acquire(): Phaser.GameObjects.Image {
    const s = this.free.pop() ?? this.make();
    s.setActive(true).setVisible(true);
    return s;
  }
  release(s: Phaser.GameObjects.Image): void {
    s.setActive(false).setVisible(false).setAngle(0).setScale(1).setAlpha(1).clearTint().setOrigin(0.5, 0.5);
    this.free.push(s);
  }
  private make(): Phaser.GameObjects.Image {
    const s = this.scene.add.image(0, 0, this.tex).setDepth(this.depth).setBlendMode(this.blend);
    this.all.push(s);
    return s;
  }
  destroyAll(): void {
    for (const s of this.all) s.destroy();
    this.all.length = 0;
    this.free.length = 0;
  }
}

export class TurretFx {
  private readonly scene: Phaser.Scene;
  /** Two turret styles (img 5 cannon / img 6 manta); a turret picks one by id. */
  private readonly bodiesManta: Pool;
  private readonly bodiesCannon: Pool;
  private readonly reticles: Pool;
  private readonly beams: Pool;
  private readonly glows: Pool;
  private readonly state = new Map<number, TState>();
  /** Per-id throttle bucket for the charge crackle (keyed off elapsedMs). */
  private readonly lastSpark = new Map<number, number>();
  /** Shared red/white spark emitter (explode mode) for charge + fire. */
  private emitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.bodiesManta = new Pool(scene, HAZARD_TEX.turret, BODY_DEPTH);
    this.bodiesCannon = new Pool(scene, HAZARD_TEX.turretCannon, BODY_DEPTH);
    this.reticles = new Pool(scene, HAZARD_TEX.target, RETICLE_DEPTH);
    this.beams = new Pool(scene, HAZARD_TEX.beam, BEAM_DEPTH, Phaser.BlendModes.ADD);
    this.glows = new Pool(scene, HAZARD_TEX.spark, GLOW_DEPTH, Phaser.BlendModes.ADD);
  }

  /**
   * Paint one turret hazard for the current world time. Call once per live turret
   * segment each `sync`; positions track the descriptor (which core advances) so
   * the visual is exactly the world the server re-simulates.
   *
   * @param h         the turret beam (family `turret`, `aimPlayer` set).
   * @param elapsedMs the world clock.
   * @param px,py     the live player centre (the reticle rides this while tracking).
   */
  update(h: SegmentHazard, elapsedMs: number, px: number, py: number, view: Viewport): void {
    const lockAt = h.bornMs + (h.aimLockMs ?? h.telegraphMs);
    const fireAt = h.bornMs + h.telegraphMs;
    const endAt = fireAt + h.activeMs;

    let st = this.state.get(h.id);
    if (!st) {
      // Alternate the two turret styles (cannon / manta) by id so a multi-turret
      // attack fields a mix — img 5 + img 6.
      const bodyPool = h.id % 2 === 1 ? this.bodiesCannon : this.bodiesManta;
      st = {
        body: bodyPool.acquire(),
        bodyPool,
        reticle: this.reticles.acquire(),
        beam: this.beams.acquire(),
        glow: this.glows.acquire(),
        locked: null,
        fired: false,
      };
      this.state.set(h.id, st);
    }

    // Aim angle (muzzle → far end). The turret art points UP (-y), so the body
    // rotation is the aim angle + 90°.
    const ang = Math.atan2(h.y2 - h.y1, h.x2 - h.x1);
    const dirX = Math.cos(ang);
    const dirY = Math.sin(ang);

    // The turret is MOUNTED at a FIXED spot — the (constant) core anchor clamped to the
    // padded edge. It only ROTATES to track; it must NEVER slide around the arena. The
    // DRAWN beam exits the BARREL TIP (fixed mount + barrel reach along the aim) and
    // runs straight out along the aim, so the lance leaves the barrel. (Collision still
    // uses the full core segment.)
    const pad = TURRET_BODY.pad; // shared with core collision so body + hit circle align
    const bodyX = Math.min(Math.max(h.x1, pad), Math.max(pad, view.width - pad));
    const bodyY = Math.min(Math.max(h.y1, pad), Math.max(pad, view.height - pad));
    const mx = bodyX + dirX * BARREL_LEN; // barrel tip = beam root + muzzle FX
    const my = bodyY + dirY * BARREL_LEN;
    const reach = Math.hypot(view.width, view.height) * 1.2;
    const ex = mx + dirX * reach; // beam runs straight out of the barrel along the aim
    const ey = my + dirY * reach;

    st.body.setPosition(bodyX, bodyY).setRotation(ang + Math.PI / 2).setVisible(true);

    if (elapsedMs < lockAt) {
      // ---- TRACK: faint targeting line + reticle following the player ----
      this.drawBeamSeg(st.beam, mx, my, ex, ey, h.thickness, 1);
      st.beam.setTint(HAZARD_COLORS.laserGlow).setAlpha(0.12);
      st.body.clearTint().setAlpha(0.92);
      st.reticle
        .setPosition(px, py)
        .setVisible(true)
        .setScale(0.95)
        .setRotation(elapsedMs / 280)
        .clearTint()
        .setAlpha(0.9);
      st.glow.setVisible(false);
      st.locked = null;
    } else if (elapsedMs < fireAt) {
      // ---- CHARGE: aim frozen; reticle locks, beam pulses, muzzle charges ----
      if (!st.locked) st.locked = { x: px, y: py }; // snapshot at the lock frame
      const p = (elapsedMs - lockAt) / Math.max(1, fireAt - lockAt); // 0..1
      const pulse = 0.5 + 0.5 * Math.sin(elapsedMs / 45);

      this.drawBeamSeg(st.beam, mx, my, ex, ey, h.thickness, 1);
      st.beam.setTint(HAZARD_COLORS.laserCore).setAlpha((0.16 + 0.38 * p) * (0.8 + 0.2 * pulse));

      st.reticle
        .setPosition(st.locked.x, st.locked.y)
        .setVisible(true)
        .setScale(1 - 0.3 * p)
        .setRotation(elapsedMs / 110)
        .setTint(0xff5a5a)
        .setAlpha(0.8 + 0.2 * pulse);

      st.glow
        .setPosition(mx, my)
        .setVisible(true)
        .setTint(HAZARD_COLORS.laserCore)
        .setScale((0.5 + 1.7 * p) * (0.92 + 0.08 * pulse))
        .setAlpha(0.35 + 0.5 * p);

      st.body.setTint(0xffb3b3).setAlpha(1);
      this.chargeSparks(h.id, mx, my, elapsedMs);
    } else if (elapsedMs < endAt) {
      // ---- FIRE: full lethal lance + muzzle flash + forward spark burst ----
      if (!st.fired) {
        st.fired = true;
        this.fireBurst(mx, my, ang);
      }
      const fp = (elapsedMs - fireAt) / Math.max(1, h.activeMs); // 0..1
      const widthMul = fp < 0.12 ? 1.7 : 1; // snap wide on ignition

      this.drawBeamSeg(st.beam, mx, my, ex, ey, h.thickness, widthMul);
      st.beam.clearTint().setAlpha(1);

      st.glow
        .setPosition(mx, my)
        .setVisible(true)
        .setTint(HAZARD_COLORS.laserHot)
        .setScale(2.4 * Math.max(0.001, 1 - fp * 1.4))
        .setAlpha(Math.max(0, 1 - fp * 2));

      const lp = st.locked ?? { x: px, y: py };
      st.reticle
        .setVisible(fp < 0.45)
        .setPosition(lp.x, lp.y)
        .setTint(HAZARD_COLORS.laserHot)
        .setScale(1 + fp * 1.6)
        .setAlpha(Math.max(0, 1 - fp * 2.2));

      st.body.clearTint().setAlpha(1);
    } else {
      st.body.setVisible(false);
      st.beam.setVisible(false);
      st.reticle.setVisible(false);
      st.glow.setVisible(false);
    }
  }

  /** Release any turret whose id is no longer live this step. */
  sweep(alive: Set<number>): void {
    for (const [id, st] of this.state) {
      if (alive.has(id)) continue;
      this.releaseState(st);
      this.state.delete(id);
      this.lastSpark.delete(id);
    }
  }

  /** Reclaim everything (on `scene.restart()`); reusable afterwards. */
  reset(): void {
    for (const st of this.state.values()) this.releaseState(st);
    this.state.clear();
    this.lastSpark.clear();
    this.emitter?.killAll();
  }

  /** Destroy all render objects (scene shutdown). */
  destroy(): void {
    this.state.clear();
    this.lastSpark.clear();
    this.emitter?.destroy();
    this.emitter = undefined;
    this.bodiesManta.destroyAll();
    this.bodiesCannon.destroyAll();
    this.reticles.destroyAll();
    this.beams.destroyAll();
    this.glows.destroyAll();
  }

  /* --------------------------------------------------------------------- */

  /** Stretch the beam strip between two explicit points with a width multiplier. */
  private drawBeamSeg(
    sprite: Phaser.GameObjects.Image,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    thickness: number,
    widthMul: number,
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    sprite
      .setVisible(true)
      .setOrigin(0.5, 0.5)
      .setPosition((x1 + x2) / 2, (y1 + y2) / 2)
      .setRotation(Math.atan2(dy, dx) + Math.PI / 2)
      .setScale((thickness * widthMul) / BEAM_TEX_W, len / BEAM_TEX_H);
  }

  private releaseState(st: TState): void {
    st.bodyPool.release(st.body);
    this.reticles.release(st.reticle);
    this.beams.release(st.beam);
    this.glows.release(st.glow);
  }

  /** Build the shared red/white spark emitter once, on first use. */
  private ensureEmitter(): Phaser.GameObjects.Particles.ParticleEmitter {
    if (!this.emitter) {
      this.emitter = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          frequency: -1,
          lifespan: { min: 220, max: 480 },
          speed: { min: 60, max: 240 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.7, end: 0, ease: 'quad.out' },
          alpha: { start: 1, end: 0, ease: 'quad.in' },
          // White-hot → red → deep ember.
          color: [HAZARD_COLORS.laserHot, HAZARD_COLORS.laserCore, HAZARD_COLORS.blastDeep],
          colorEase: 'quad.out',
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        })
        .setDepth(GLOW_DEPTH);
      this.emitter.reserve(64);
    }
    return this.emitter;
  }

  /** A small crackle of sparks at the muzzle while charging (throttled per id). */
  private chargeSparks(id: number, x: number, y: number, elapsedMs: number): void {
    const bucket = Math.floor(elapsedMs / CHARGE_INTERVAL_MS);
    if (this.lastSpark.get(id) === bucket) return;
    this.lastSpark.set(id, bucket);
    const e = this.ensureEmitter();
    e.updateConfig({ speed: { min: 30, max: 110 }, angle: { min: 0, max: 360 } });
    e.explode(CHARGE_SPARKS, x, y);
  }

  /** The fire flash: a forward-biased spark burst out of the muzzle along the beam. */
  private fireBurst(x: number, y: number, ang: number): void {
    const e = this.ensureEmitter();
    const deg = (ang * 180) / Math.PI;
    e.updateConfig({ speed: { min: 140, max: 360 }, angle: { min: deg - 32, max: deg + 32 } });
    e.explode(FIRE_SPARKS, x, y);
  }
}
